import { useFormikContext } from "formik";
import React, { FC } from "react";
import CheckboxNode2 from "./checkbox-node2";

export type Tree = {
    id: string;
    label: string;
    /**
     * The lookupname is used to identify the node in the tree.
     * It should be unique among sibling nodes.
     * Pattern example: grandparent/parent/node
     */
    lookupname: string;
    /**
     * If clickable is false, the checkbox will not be shown for that node.
     * Default is undefined which will result to false.
     */
    clickable?: boolean;
    /**
     * If checked is true, the checkbox will be checked.
     * Default is undefined which will result to false.
     */
    checked?: boolean;
    children?: Tree[];
}

interface CheckboxTree2Props {
    /**
     * The tree data to be rendered initially.
     */
    tree: Tree[];
    /**
     * Optional name for the tree, used for form integration with formik.
     */
    name?: string;
}

/**
 * CheckboxTree2 component for rendering a tree of checkboxes. If this is component is used with formik
 * the name prop must be provided and the formik value will be presented as tree state. The parent component
 * will be responsible to manipulate the formik value to match it requirements.
 * @param tree The tree data to be rendered initially.
 * @param name Optional name for the tree, used for form integration with formik.
 * @returns 
 */
const CheckboxTree2: FC<CheckboxTree2Props> = ({ tree, name }) => {
    const [treeState, setTreeState] = React.useState<Tree[]>(tree);
    const { setFieldValue } = useFormikContext<any>();

    const updateParentCheckedState = (tree: Tree[], lookupname: string) => {
        const keys = lookupname.split('/');
        for (let i = keys.length - 2; i >= 0; i--) {
            const parentLookup = keys.slice(0, i + 1).join('/');
            const parentNode = findNodeByLookupname(tree, parentLookup);
            if (parentNode && parentNode.children) {
                const anyChecked = parentNode.children.some(child => child.checked);
                parentNode.checked = anyChecked;
            }
        }
    };

    const setCheckedRecursive = (node: Tree, checked: boolean) => {
        node.checked = checked;
        if (node.children) {
            node.children.forEach(child => setCheckedRecursive(child, checked));
        }
    };

    const findNodeByLookupname = (nodes: Tree[], lookupname: string): Tree | undefined => {
        for (const node of nodes) {
            if (node.lookupname === lookupname) return node;
            if (node.children) {
                const found = findNodeByLookupname(node.children, lookupname);
                if (found) return found;
            }
        }
        return undefined;
    };

    const onCheck = (_: React.ChangeEvent<HTMLInputElement>, node: Tree) => {
        const keys: string[] = node.lookupname.split('/');
        let currentNodes: Tree[] = tree;
        const isChecked: boolean = !node.checked;

        for (const key of keys) {
            const lookupname: string = keys.slice(0, keys.indexOf(key) + 1).join('/');
            const found = currentNodes.find(node => node.lookupname === lookupname);
            currentNodes = found?.children ?? [];

            if (lookupname === node.lookupname && found) {
                setCheckedRecursive(found, isChecked);
            }
        }

        // Update parent checked state after changing the node
        updateParentCheckedState(tree, node.lookupname);

        const newTree = [...treeState];
        name && setFieldValue(name, newTree);
        setTreeState(newTree);
    };

    return <div className={`flex flex-col text-sm`}>
        {tree.map((node) => {
            return <CheckboxNode2 key={node.id} tree={node} onChange={onCheck} />
        })}
    </div>
}

export default CheckboxTree2;